export type MapPadding = { top: number; bottom: number; left: number; right: number };

type Measurable = Pick<HTMLElement, 'offsetHeight'>;

/**
 * Computes camera padding for the route overview from the real on-screen layout, so the route is framed
 * inside the part of the map not covered by the directions header (top) and the Steps | Go bar (bottom).
 * Header height varies (stop count, logo, collapsed inputs), so it can't be a constant.
 *
 * A side whose element is missing or has no height falls back to `fallback`.
 *
 * @param mapElement - the map render element; without it nothing is measured and `fallback` is used
 * @param headerElement - the directions header overlaying the top of the map
 * @param bottomBarElement - the preview bar overlaying the bottom of the map
 * @param fallback - padding to use for any side that can't be measured
 * @param margin - extra space kept clear beyond each measured element (e.g. to fit the start marker)
 */
export const measureOverviewPadding = ({
  mapElement,
  headerElement,
  bottomBarElement,
  fallback,
  margin,
}: {
  mapElement: Measurable | null;
  headerElement: Measurable | null;
  bottomBarElement: Measurable | null;
  fallback: MapPadding;
  margin: { top: number; bottom: number };
}): MapPadding => {
  if (!mapElement) {
    return fallback;
  }
  // offsetHeight is the layout height, unaffected by slide-in transforms, so it is correct mid-animation.
  const headerHeight = headerElement?.offsetHeight ?? 0;
  const barHeight = bottomBarElement?.offsetHeight ?? 0;

  return {
    ...fallback,
    top: headerHeight > 0 ? headerHeight + margin.top : fallback.top,
    bottom: barHeight > 0 ? barHeight + margin.bottom : fallback.bottom,
  };
};
